import React from 'react';
import * as XLSX from 'xlsx';
import { RowData } from '../App';
import { Block } from '../classes/Block';
import { VideoSettings } from './VideoSettings';
import { t } from '@lingui/core/macro';
import { auth } from '../firebase';
import {
  buildTableData,
  PROJECT_SHEET_NAMES,
  splitTableData,
  TableMatrix,
} from '../utils/excelTable';
import type { CardGroup } from '../utils/cardGroups';
import type { DefaultCardSettings } from '../utils/defaultCardSettings';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import { sanitizeFileNameSegment } from '../utils/stringSanitization';
import { useProject } from '../contexts/ProjectContext';

// Add type declarations for File System Access API
declare global {
  interface Window {
    showDirectoryPicker(): Promise<FileSystemDirectoryHandle>;
  }
}

interface FileSystemDirectoryHandle {
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<FileSystemDirectoryHandle>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<FileSystemFileHandle>;
}

interface FileSystemFileHandle {
  createWritable(): Promise<FileSystemWritableFileStream>;
}

interface FileSystemWritableFileStream {
  write(data: Blob): Promise<void>;
}

interface ExcelExporterProps {
  rowData: RowData[];
  columnDefs: any[];
  sheetName: string;
  blocks?: Block[];
  videoSettings: VideoSettings;
  cardGroups?: CardGroup[];
  defaultCardSettings?: DefaultCardSettings;
}

// Utility function to save to Google Sheets - can be called from outside the component
export const saveToGoogleSheets = async (aoaData: TableMatrix, sheetName: string, userEmail: string | null, projectId?: string) => {
  if (!userEmail) {
    console.log('[GSHEETS] No user email available, skipping Google Sheets save');
    return;
  }

  try {
    const response = await authFetch(`${getApiUrl()}/save-google-sheets?projectId=${projectId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        values: aoaData,
        sheetName: sheetName || 'Sheet1'
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to save to Google Sheets');
    }

    const result = await response.json();
    console.log('[GSHEETS] Saved to Google Sheets:', result);
  } catch (error) {
    console.error('[GSHEETS] Error saving to Google Sheets:', error);
    throw error;
  }
};

const ExcelExporter: React.FC<ExcelExporterProps> = ({
  rowData,
  columnDefs,
  sheetName,
  blocks = [],
  videoSettings,
  cardGroups = [],
  defaultCardSettings,
}) => {
  const { selectedProject } = useProject();

  const buildExportFileName = (): string => {
    const sanitizedProjectName: string = sanitizeFileNameSegment(selectedProject?.name, {
      fallback: 'project',
      lowercase: false,
      replacements: { ' ': '_', ':': 'x' },
      invalidReplacement: '_',
    });

    const now: Date = new Date();
    const day: string = now.getDate().toString().padStart(2, '0');
    const month: string = (now.getMonth() + 1).toString().padStart(2, '0');
    const year: string = now.getFullYear().toString();
    const hours: string = now.getHours().toString().padStart(2, '0');
    const minutes: string = now.getMinutes().toString().padStart(2, '0');

    const timestamp: string = `${day}-${month}-${year} ${hours}-${minutes}`;
    return `${sanitizedProjectName} ${timestamp}.xlsx`;
  };

  const exportToExcel = async () => {
    if (rowData.length === 0 || columnDefs.length === 0) {
      alert('No data to export');
      return;
    }

    try {
      const aoaData: TableMatrix = buildTableData(
        blocks,
        columnDefs,
        videoSettings,
        cardGroups,
        defaultCardSettings
      );
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      const sheetTables = splitTableData(aoaData);
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(sheetTables.data), PROJECT_SHEET_NAMES.data);
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(sheetTables.blockProperties),
        PROJECT_SHEET_NAMES.blockProperties
      );
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(sheetTables.videoProperties),
        PROJECT_SHEET_NAMES.videoProperties
      );

      const fileName: string = buildExportFileName();
      
      // Generate Excel file for download
      XLSX.writeFile(workbook, fileName);

      // Also save to Google Sheets via the unified endpoint
      try {
        const userEmail: string | null = auth.currentUser?.email ?? null;
        if (userEmail) {
          await saveToGoogleSheets(aoaData, sheetName, userEmail, selectedProject!.id);
        }
      } catch (gsErr) {
        console.error('Error saving to Google Sheets:', gsErr);
      }
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export data to Excel.');
    }
  };

  return (
    <div className="excel-exporter">
      <button 
        onClick={exportToExcel} 
        disabled={rowData.length === 0}
        className="export-button"
      >
        {t`Backup to Excel`}
      </button>
    </div>
  );
};

export default ExcelExporter;
